import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, XCircle, MessageSquare, FileText, Building2, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { formatIndianDate, formatCurrencyINR } from "../utils/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  draft: "Draft",
  sent: "Sent",
  viewed: "Viewed",
  pending_response: "Pending response",
  self_approved: "Approved",
  client_approved_registered: "Approved",
  client_approved_invited: "Approved",
  seller_marked_approved: "Approved",
  rejected: "Rejected",
  revision_requested: "Revision requested",
  converted_to_invoice: "Converted to invoice",
  expired: "Expired",
};

export default function PublicQuotationPage() {
  const { shareToken } = useParams();
  const [q, setQ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState("");
  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(null); // 'reject' | 'request_changes' | null

  useEffect(() => {
    axios.get(`${API}/quotations/public/${shareToken}`)
      .then(r => setQ(r.data))
      .catch(err => toast.error(err.response?.data?.detail || "Quotation not found"))
      .finally(() => setLoading(false));
  }, [shareToken]);

  const act = async (action) => {
    setActing(action);
    try {
      const res = await axios.post(`${API}/quotations/public/${shareToken}/action`, {
        action, note, registered: false,
      });
      setQ(prev => ({ ...prev, status: res.data.status }));
      toast.success(
        action === "accept" ? "Thank you — quotation accepted" :
        action === "reject" ? "Quotation rejected" :
        "Revision requested"
      );
      setShowNote(null);
      setNote("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally { setActing(""); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!q) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 text-center" data-testid="public-quote-missing">
        <div>
          <FileText className="w-10 h-10 text-slate-400 mx-auto mb-3" />
          <p className="text-slate-700 font-medium">This quotation link is invalid or expired.</p>
          <p className="text-sm text-slate-500 mt-1">Please ask the sender for a new link.</p>
        </div>
      </div>
    );
  }

  const decisionMade = ["client_approved_registered", "client_approved_invited", "self_approved", "seller_marked_approved", "rejected", "revision_requested", "converted_to_invoice", "expired"].includes(q.status);
  const isApproved = ["client_approved_registered", "client_approved_invited", "self_approved", "seller_marked_approved", "converted_to_invoice"].includes(q.status);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-6 sm:py-10 px-4" data-testid="public-quote-page">
      <div className="max-w-3xl mx-auto">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <img src="/assets/logo.png" alt="" className="w-8 h-8 rounded-lg" />
            <span className="text-sm font-bold text-slate-800">InvoiceSetu</span>
          </div>
          <span className="text-[11px] inline-flex items-center gap-1 text-slate-500">
            <ShieldCheck className="w-3.5 h-3.5" /> Secure quotation link
          </span>
        </div>

        {/* Header card */}
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-5"
        >
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Quotation</p>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-0.5" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="public-q-number">
                {q.quotation_number}
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Issued {formatIndianDate(q.quotation_date)}
                {q.valid_until && <> · Valid until {formatIndianDate(q.valid_until)}</>}
              </p>
            </div>
            <span
              className={`text-[11px] font-bold uppercase px-3 py-1 rounded-full self-start ${
                isApproved ? "bg-emerald-50 text-emerald-700"
                : q.status === "rejected" ? "bg-red-50 text-red-700"
                : q.status === "revision_requested" ? "bg-orange-50 text-orange-700"
                : q.status === "expired" ? "bg-slate-100 text-slate-500"
                : "bg-blue-50 text-blue-700"
              }`}
              data-testid="public-q-status"
            >
              {STATUS_LABEL[q.status] || q.status}
            </span>
          </div>
        </motion.div>

        {/* Parties */}
        <div className="grid sm:grid-cols-2 gap-4 mb-5">
          <Card title="From">
            <p className="font-semibold text-slate-900">Vendor Quote</p>
            <p className="text-xs text-slate-500 mt-1">Submitted via InvoiceSetu</p>
          </Card>
          <Card title="To">
            <p className="font-semibold text-slate-900">{q.customer_name}</p>
            {q.customer_address && <p className="text-xs text-slate-500 mt-0.5">{q.customer_address}</p>}
            {q.customer_email && <p className="text-xs text-slate-500 mt-0.5">{q.customer_email}</p>}
            {q.customer_phone && <p className="text-xs text-slate-500 mt-0.5">{q.customer_phone}</p>}
            {q.customer_gst && <p className="text-[11px] font-mono text-slate-400 mt-0.5">GSTIN: {q.customer_gst}</p>}
          </Card>
        </div>

        {/* Items */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden mb-5">
          <div className="p-5 border-b border-slate-100 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-slate-400" />
            <h3 className="text-sm font-semibold text-slate-900">Line Items</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">Item</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">Qty</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">Rate</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">GST</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">Total</th>
                </tr>
              </thead>
              <tbody>
                {(q.items || []).map((it, i) => {
                  const base = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
                  const total = it.gst_not_applicable ? base : base * (1 + (Number(it.gst_percent) || 0) / 100);
                  return (
                    <tr key={i} className="border-b border-slate-50 last:border-0">
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900">{it.name}</p>
                        {it.description && <p className="text-xs text-slate-500 mt-0.5">{it.description}</p>}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-700">{it.quantity}</td>
                      <td className="px-4 py-3 text-right text-slate-700">{formatCurrencyINR(it.unit_price)}</td>
                      <td className="px-4 py-3 text-right text-slate-700">{it.gst_not_applicable ? "—" : `${it.gst_percent}%`}</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900">{formatCurrencyINR(total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="bg-slate-50 border-t border-slate-100 p-5">
            <div className="ml-auto max-w-xs space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{formatCurrencyINR(q.subtotal)}</span></div>
              {q.tax_amount > 0 && <div className="flex justify-between text-slate-600"><span>GST</span><span>{formatCurrencyINR(q.tax_amount)}</span></div>}
              <div className="flex justify-between pt-2 border-t border-slate-200 text-base font-bold text-slate-900">
                <span>Grand Total</span><span data-testid="public-q-grand">{formatCurrencyINR(q.grand_total)}</span>
              </div>
            </div>
          </div>
        </div>

        {(q.notes || q.terms) && (
          <div className="grid sm:grid-cols-2 gap-4 mb-5">
            {q.notes && <Card title="Notes"><p className="text-sm text-slate-700 whitespace-pre-line">{q.notes}</p></Card>}
            {q.terms && <Card title="Terms & Conditions"><p className="text-sm text-slate-700 whitespace-pre-line">{q.terms}</p></Card>}
          </div>
        )}

        {/* Action card */}
        {!decisionMade ? (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 sm:p-6"
            data-testid="public-q-action-card"
          >
            <h3 className="text-base font-semibold text-slate-900 mb-1">Your decision</h3>
            <p className="text-xs text-slate-500 mb-4">Approve to confirm. Reject if not interested. Or ask for changes.</p>

            {showNote && (
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder={showNote === "reject" ? "Optional reason" : "What changes do you need?"}
                rows={3}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
                data-testid="public-q-note"
              />
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => act("accept")}
                disabled={!!acting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="public-q-accept"
              >
                {acting === "accept" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                Approve quotation
              </Button>
              <Button
                onClick={() => showNote === "request_changes" ? act("request_changes") : setShowNote("request_changes")}
                disabled={!!acting}
                variant="outline"
                className="border-orange-200 text-orange-700 hover:bg-orange-50"
                data-testid="public-q-request"
              >
                {acting === "request_changes" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <MessageSquare className="w-4 h-4 mr-1" />}
                {showNote === "request_changes" ? "Send change request" : "Request changes"}
              </Button>
              <Button
                onClick={() => showNote === "reject" ? act("reject") : setShowNote("reject")}
                disabled={!!acting}
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50"
                data-testid="public-q-reject"
              >
                {acting === "reject" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
                {showNote === "reject" ? "Confirm rejection" : "Reject"}
              </Button>
              {showNote && (
                <Button onClick={() => { setShowNote(null); setNote(""); }} variant="ghost" className="text-slate-600">
                  Cancel
                </Button>
              )}
            </div>
          </motion.div>
        ) : (
          <div className={`rounded-2xl p-5 text-center ${
            isApproved ? "bg-emerald-50 border border-emerald-200" :
            q.status === "rejected" ? "bg-red-50 border border-red-200" :
            "bg-orange-50 border border-orange-200"
          }`} data-testid="public-q-decision-banner">
            {isApproved ? <CheckCircle2 className="w-7 h-7 text-emerald-600 mx-auto" /> :
             q.status === "rejected" ? <XCircle className="w-7 h-7 text-red-600 mx-auto" /> :
             <MessageSquare className="w-7 h-7 text-orange-600 mx-auto" />}
            <p className="font-semibold text-slate-900 mt-2">
              {isApproved ? "Thank you — your approval is recorded" :
               q.status === "rejected" ? "Quotation marked as rejected" :
               "Revision request submitted"}
            </p>
            <p className="text-xs text-slate-600 mt-1">The vendor has been notified and will follow up.</p>
          </div>
        )}

        <p className="text-center text-[11px] text-slate-400 mt-6">
          Powered by InvoiceSetu · Anyone with this link can view and act on this quotation.
        </p>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
      <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">{title}</p>
      {children}
    </div>
  );
}
